import React, { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext } from 'react-oauth2-code-pkce';
import GlobalContext from '../context/GlobalContext';
import CompaniesService from '../services/companies-service';
import AdminShell from './AdminShell';
import DeniedCard from './DeniedCard';
import { IconEdit, IconTrash } from './Icons';

export default function CompanyDetailPage() {
  const { companyId } = useParams();
  const { currentUser, userLoading } = useContext(GlobalContext);
  const { token } = useContext(AuthContext);
  
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ name: '' });
  const [saving, setSaving] = useState(false);

  // Fetch company details on component mount
  useEffect(() => {
    if (token && currentUser?.isAdmin && companyId) {
      fetchCompanyDetails();
    }
  }, [token, currentUser, companyId]);

  // Update edit data when company changes
  useEffect(() => {
    if (company) {
      setEditData({ name: company.name || '' });
    }
  }, [company]);

  const fetchCompanyDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const companyData = await CompaniesService.getCompanyById(companyId, token);
      setCompany(companyData);
    } catch (err) {
      setError('Fout bij laden van bedrijfsgegevens: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editData.name.trim()) {
      setError('Bedrijfsnaam is verplicht');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const updatedCompany = {
        ...company,
        name: editData.name.trim()
      };

      await CompaniesService.updateCompany(updatedCompany, token);
      await fetchCompanyDetails();
      setIsEditing(false);
      setSuccessMessage('Bedrijfsgegevens succesvol bijgewerkt!');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError('Fout bij opslaan van bedrijfsgegevens: ' + (err.message || err));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (company) {
      setEditData({ name: company.name || '' });
    }
    setError(null);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Weet je zeker dat je het bedrijf "${company.name}" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`)) {
      return;
    }

    try {
      await CompaniesService.deleteCompany(company.id, token);
      // Redirect to company list
      window.location.href = '/bedrijven';
    } catch (err) {
      setError('Fout bij verwijderen van bedrijf: ' + (err.message || err));
    }
  };

  if (userLoading || loading) {
    return (
      <div className="page-shell flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!currentUser?.isAdmin) {
    return <DeniedCard />;
  }

  return (
    <AdminShell
      title="Bedrijfsdetails"
      backHref="/bedrijven"
      backLabel="Bedrijven"
      actions={(
        <button type="button" onClick={fetchCompanyDetails} disabled={loading} className="btn-ghost">
          Vernieuwen
        </button>
      )}
    >
        {/* Error message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Success message */}
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
            {successMessage}
          </div>
        )}

        {!company && !loading ? (
          <div className="text-center py-12">
            <span className="material-icons-outlined text-gray-400 text-6xl mb-4">business_center</span>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Bedrijf niet gevonden</h3>
            <p className="text-gray-600 mb-6">Het opgegeven bedrijf bestaat niet of kon niet worden geladen.</p>
            <a 
              href="/bedrijven"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Terug naar bedrijvenlijst
            </a>
          </div>
        ) : company && (
          <div className="space-y-6">
            {/* Company Overview Card */}
            <div className="card p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <div className="w-16 h-16 bg-gray-300 rounded-lg flex items-center justify-center mr-4">
                    <span className="material-icons-outlined text-gray-600 text-2xl">business</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editData.name}
                          onChange={(e) => setEditData({ name: e.target.value })}
                          disabled={saving}
                          className="text-2xl font-bold bg-transparent border-b-2 border-blue-500 focus:outline-none focus:border-blue-600 disabled:opacity-50"
                          placeholder="Bedrijfsnaam"
                        />
                      ) : (
                        company.name || 'Onbekend bedrijf'
                      )}
                    </h2>
                    <div className="flex gap-2 mt-2">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Actief
                      </span>
                      {isEditing && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          Bewerken
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div className="card">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Bedrijfsinformatie</h3>
              </div>
              <div className="px-6 py-4">
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Naam</dt>
                    <dd className="text-sm text-gray-900">{company.name || 'Onbekend'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Aangemaakt op</dt>
                    <dd className="text-sm text-gray-900">
                      {company.createdAt 
                        ? new Date(company.createdAt).toLocaleString('nl-NL')
                        : 'Onbekend'
                      }
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Actions */}
            <div className="card p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Acties</h3>
              {isEditing ? (
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleSave}
                    disabled={saving || !editData.name.trim()}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:bg-gray-400 flex items-center"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Opslaan...
                      </>
                    ) : (
                      <>
                        <span className="material-icons-outlined mr-2 text-sm">save</span>
                        Opslaan
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors disabled:opacity-50"
                  >
                    <span className="material-icons-outlined mr-2 text-sm">cancel</span>
                    Annuleren
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setIsEditing(true)}
                    disabled={saving}
                    className="btn-primary"
                  >
                    <IconEdit size={16} />
                    Bewerken
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400"
                  >
                    <IconTrash size={16} />
                    Verwijderen
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
    </AdminShell>
  );
}
